import React, { useState, useEffect } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { getQueueCount, clearStaleRequests, clearQueue } from '../utils/requestQueue';
import offlineSyncService from '../services/offlineSync';

const ConnectionStatus = () => {
  const { isOnline, wasOffline } = useOnlineStatus();
  const [queueCount, setQueueCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [dismissed, setDismissed] = useState(false); // Allow user to hide the top band
  const barHeightPx = 56;

  useEffect(() => {
    // Update queue count periodically
    const updateQueueCount = async () => {
      const count = await getQueueCount();
      setQueueCount(count);
    };

    updateQueueCount();
    const interval = setInterval(updateQueueCount, 2000);
    
    // Clear stale requests on mount (older than 24 hours)
    clearStaleRequests(24).then((cleared) => {
      if (cleared > 0) {
        console.log(`Cleared ${cleared} stale requests`);
        updateQueueCount();
      }
    });

    // Listen for retry events from both old system and new sync service
    const handleRetry = () => {
      setIsRetrying(true);
      setTimeout(() => {
        setIsRetrying(false);
        updateQueueCount();
      }, 2000);
    };

    const handleSyncComplete = () => {
      setIsRetrying(true);
      setTimeout(() => {
        setIsRetrying(false);
        updateQueueCount();
      }, 2000);
    };

    window.addEventListener('queuedRequestsRetried', handleRetry);
    window.addEventListener('offlineSyncComplete', handleSyncComplete);

    // Listen to sync service status
    const unsubscribe = offlineSyncService.addSyncListener((event) => {
      if (event.type === 'sync_start') {
        setIsRetrying(true);
      } else if (event.type === 'sync_complete') {
        setIsRetrying(false);
        updateQueueCount();
      }
    });

    return () => {
      clearInterval(interval);
      window.removeEventListener('queuedRequestsRetried', handleRetry);
      window.removeEventListener('offlineSyncComplete', handleSyncComplete);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleClearQueue = async () => {
    setIsClearing(true);
    try {
      await clearQueue();
      setQueueCount(0);
      console.log('Queue cleared');
    } catch (error) {
      console.error('Error clearing queue:', error);
    } finally {
      setIsClearing(false);
    }
  };

  const handleForceSync = async () => {
    setIsRetrying(true);
    try {
      await offlineSyncService.forceSync();
      await new Promise(resolve => setTimeout(resolve, 1000));
      const count = await getQueueCount();
      setQueueCount(count);
    } catch (error) {
      console.error('Error forcing sync:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleClose = () => {
    // Just hide the bar until page reload / route change
    setDismissed(true);
  };

  // Don't show if user closed it manually, or if online, no queued requests, and wasn't recently offline
  const shouldShow = !(dismissed || (isOnline && queueCount === 0 && !wasOffline && !isRetrying));

  // IMPORTANT: This banner is `position: fixed` and can block header buttons on mobile.
  // Add body padding while visible so underlying UI (Back/Switch Role/etc.) remains clickable.
  useEffect(() => {
    if (!shouldShow) return;
    const prev = document.body.style.paddingTop;
    document.body.style.paddingTop = `${barHeightPx}px`;
    return () => {
      document.body.style.paddingTop = prev;
    };
  }, [shouldShow, barHeightPx]);

  if (!shouldShow) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: isOnline ? '#10b981' : '#ef4444',
        color: 'white',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '600',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {isOnline ? (
          <>
            {isRetrying ? (
              <>
                <span>🔄</span>
                <span>Syncing queued requests...</span>
              </>
            ) : wasOffline ? (
              <>
                <span>✅</span>
                <span>Connection restored</span>
              </>
            ) : queueCount > 0 ? (
              <>
                <span>⏳</span>
                <span>{queueCount} request{queueCount !== 1 ? 's' : ''} queued for sync</span>
                <button
                  onClick={handleForceSync}
                  disabled={isRetrying}
                  style={{
                    marginLeft: '12px',
                    padding: '4px 12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '4px',
                    color: 'white',
                    cursor: isRetrying ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                  }}
                  title="Force sync now"
                >
                  {isRetrying ? 'Syncing...' : 'Sync Now'}
                </button>
                <button
                  onClick={handleClearQueue}
                  disabled={isClearing}
                  style={{
                    marginLeft: '8px',
                    padding: '4px 12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '4px',
                    color: 'white',
                    cursor: isClearing ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                  }}
                  title="Clear all queued requests"
                >
                  {isClearing ? 'Clearing...' : 'Clear'}
                </button>
              </>
            ) : (
              <>
                <span>✅</span>
                <span>Connected</span>
              </>
            )}
          </>
        ) : (
          <>
            <span>📡</span>
            <span>No internet connection. Requests will be queued.</span>
          </>
        )}
      </div>

      {/* Close (X) button to hide the band */}
      <button
        onClick={handleClose}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: 'bold',
          lineHeight: 1,
        }}
        title="Hide this bar"
      >
        ×
      </button>
    </div>
  );
};

export default ConnectionStatus;

